import React from 'react';
import {
  Box,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  Place as MarkerIcon,
  Grain as ClusterIcon,
  Whatshot as HeatmapIcon,
  Public as BoundaryIcon
} from '@mui/icons-material';

const LayerControl = ({ layers, onLayerToggle }) => {
  const layerItems = [
    {
      name: 'markers',
      label: 'Individual Markers',
      icon: <MarkerIcon />,
      description: 'Show individual location markers',
      color: '#2196F3'
    },
    {
      name: 'clusters',
      label: 'Marker Clusters',
      icon: <ClusterIcon />,
      description: 'Group nearby markers into clusters',
      color: '#4CAF50'
    },
    {
      name: 'heatmap',
      label: 'Heatmap Layer',
      icon: <HeatmapIcon />,
      description: 'Show density heatmap of incidents',
      color: '#F44336'
    },
    {
      name: 'boundaries',
      label: 'District Boundaries',
      icon: <BoundaryIcon />,
      description: 'Show administrative boundaries',
      color: '#FF9800'
    }
  ];
  
  return (
    <Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Control the visibility of different map layers
      </Typography>
      
      <FormGroup>
        {layerItems.map((layer, index) => (
          <Paper
            key={layer.name}
            elevation={layers[layer.name] ? 2 : 0}
            sx={{
              p: 2,
              mb: 2,
              border: layers[layer.name] ? `2px solid ${layer.color}` : '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 3
              }
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={layers[layer.name]}
                  onChange={() => onLayerToggle(layer.name)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Box sx={{ color: layer.color }}>
                    {layer.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {layer.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {layer.description}
                    </Typography>
                  </Box>
                  {layers[layer.name] && (
                    <Chip
                      label="Active"
                      size="small"
                      sx={{
                        bgcolor: layer.color,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.65rem'
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ width: '100%', m: 0 }}
            />
          </Paper>
        ))}
      </FormGroup>
      
      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Layer Tips
        </Typography>
        <Typography variant="caption" component="div" color="textSecondary" sx={{ mb: 1 }}>
          • Markers and Clusters cannot be active simultaneously
        </Typography>
        <Typography variant="caption" component="div" color="textSecondary" sx={{ mb: 1 }}>
          • Heatmap shows incident density across the map
        </Typography>
        <Typography variant="caption" component="div" color="textSecondary">
          • Use clusters for better performance with many locations
        </Typography>
      </Box>
      
      {/* Legend */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          Marker Status Colors
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {[
            { status: 'Pending', color: '#FFA500' },
            { status: 'In Progress', color: '#2196F3' },
            { status: 'Approved', color: '#4CAF50' },
            { status: 'Completed', color: '#8BC34A' },
            { status: 'Rejected', color: '#F44336' }
          ].map(item => (
            <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              />
              <Typography variant="caption">{item.status}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LayerControl;
